import { Field } from "@/components/admin/field";
import { SectionHeader } from "@/components/dashboard/section-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { adminApi } from "@/lib/admin-api";
import { ROLE_LABELS, type Role, type UserDto } from "@ow/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

const ROLES: Role[] = ["admin", "client_viewer", "restricted_viewer"];

const selectCls = "h-9 rounded-md border border-input bg-transparent px-2 text-sm";

export function AdminUsersPage() {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ["admin", "users"] });
  const { data: users = [], isLoading } = useQuery({
    queryKey: ["admin", "users"],
    queryFn: () => adminApi.users.list(),
  });

  // Add-user form
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState<Role>("client_viewer");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      adminApi.users.create({
        email: email.trim(),
        name: name.trim() || null,
        role,
        password: password.trim() || null,
      }),
    onSuccess: () => {
      invalidate();
      setEmail("");
      setName("");
      setRole("client_viewer");
      setPassword("");
      setError(null);
    },
    onError: (e) => setError(e instanceof Error ? e.message : "Could not add user"),
  });

  const update = useMutation({
    mutationFn: (v: { id: string; body: Parameters<typeof adminApi.users.update>[1] }) =>
      adminApi.users.update(v.id, v.body),
    onSuccess: invalidate,
  });
  const remove = useMutation({ mutationFn: (id: string) => adminApi.users.remove(id), onSuccess: invalidate });

  const [pwFor, setPwFor] = useState<string | null>(null);
  const [pwValue, setPwValue] = useState("");

  const canAdd = email.trim().length > 3 && email.includes("@") && (password === "" || password.length >= 8);

  return (
    <div className="space-y-6">
      <SectionHeader title="Users & roles">
        <span className="text-xs text-muted-foreground">Magic-link sign-in lands later — roles take effect now.</span>
      </SectionHeader>

      {/* Add user */}
      <section className="rounded-xl border bg-card p-4">
        <div className="eyebrow mb-3">Add user</div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <Field label="Email">
            <Input
              type="email"
              placeholder="person@firm.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>
          <Field label="Name">
            <Input placeholder="Full name" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="Role">
            <select className={selectCls} value={role} onChange={(e) => setRole(e.target.value as Role)}>
              {ROLES.map((r) => (
                <option key={r} value={r}>
                  {ROLE_LABELS[r]}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Password (optional, 8+)">
            <Input
              type="password"
              placeholder="leave blank for magic-link"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>
        </div>
        <div className="mt-3 flex items-center gap-3">
          <Button size="sm" disabled={!canAdd || create.isPending} onClick={() => create.mutate()}>
            {create.isPending ? "Adding…" : "Add user"}
          </Button>
          {error ? <span className="text-sm text-destructive">{error}</span> : null}
        </div>
      </section>

      {/* User list */}
      <div className="overflow-x-auto rounded-xl border">
        <table className="w-full min-w-[680px] text-sm">
          <thead className="border-b bg-muted/40">
            <tr className="text-left">
              {["Email", "Name", "Role", "Password", "Active", ""].map((h) => (
                <th key={h} className="px-3 py-2 font-mono text-[11px] uppercase tracking-wide text-muted-foreground">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <Empty>Loading…</Empty>
            ) : users.length === 0 ? (
              <Empty>No users yet — add one above.</Empty>
            ) : (
              users.map((u) => (
                <UserRow
                  key={u.id}
                  user={u}
                  onRole={(r) => update.mutate({ id: u.id, body: { role: r } })}
                  onToggleActive={() => update.mutate({ id: u.id, body: { isActive: !u.isActive } })}
                  onDelete={() => remove.mutate(u.id)}
                  pwOpen={pwFor === u.id}
                  onPwOpen={() => {
                    setPwFor(u.id);
                    setPwValue("");
                  }}
                  pwValue={pwValue}
                  onPwValue={setPwValue}
                  onPwSave={() => {
                    update.mutate({ id: u.id, body: { password: pwValue } });
                    setPwFor(null);
                    setPwValue("");
                  }}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return (
    <tr>
      <td colSpan={6} className="px-3 py-8 text-center text-muted-foreground">
        {children}
      </td>
    </tr>
  );
}

function UserRow({
  user,
  onRole,
  onToggleActive,
  onDelete,
  pwOpen,
  onPwOpen,
  pwValue,
  onPwValue,
  onPwSave,
}: {
  user: UserDto;
  onRole: (r: Role) => void;
  onToggleActive: () => void;
  onDelete: () => void;
  pwOpen: boolean;
  onPwOpen: () => void;
  pwValue: string;
  onPwValue: (v: string) => void;
  onPwSave: () => void;
}) {
  return (
    <tr className="border-b align-middle last:border-0">
      <td className="px-3 py-2 font-medium">{user.email}</td>
      <td className="px-3 py-2 text-muted-foreground">{user.name ?? "—"}</td>
      <td className="px-3 py-2">
        <select className={selectCls} value={user.role} onChange={(e) => onRole(e.target.value as Role)}>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {ROLE_LABELS[r]}
            </option>
          ))}
        </select>
      </td>
      <td className="px-3 py-2">
        {pwOpen ? (
          <span className="inline-flex items-center gap-1">
            <Input
              type="password"
              autoFocus
              placeholder="8+ chars"
              value={pwValue}
              onChange={(e) => onPwValue(e.target.value)}
              className="h-8 w-28"
            />
            <Button size="xs" disabled={pwValue.length < 8} onClick={onPwSave}>
              Save
            </Button>
          </span>
        ) : (
          <button type="button" onClick={onPwOpen} className="text-xs text-muted-foreground hover:text-foreground">
            {user.hasPassword ? "Set ✓ · reset" : "— · set"}
          </button>
        )}
      </td>
      <td className="px-3 py-2">
        <button
          type="button"
          onClick={onToggleActive}
          className={`font-mono text-[11px] uppercase ${user.isActive ? "text-[var(--color-success)]" : "text-muted-foreground"}`}
        >
          {user.isActive ? "active" : "inactive"}
        </button>
      </td>
      <td className="px-3 py-2 text-right">
        <Button size="xs" variant="ghost" onClick={onDelete}>
          Delete
        </Button>
      </td>
    </tr>
  );
}
